/*
 * AccountSettingsPage.module.css
 * All colors come from themes.css custom properties — no hardcoded values.
 */

.pageWrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: var(--color-bg-primary);
}

.main {
  flex: 1;
  padding: 32px 16px 64px;
}

.container {
  max-width: 560px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.pageTitle {
  font-size: 1.75rem;
  font-family: var(--font-family-base);
  color: var(--color-text-primary);
  margin: 0 0 4px;
}

.usernameDisplay {
  font-size: 0.95rem;
  color: var(--color-text-muted);
  margin: 0;
}

.statusText {
  color: var(--color-text-secondary);
}

/* ── Card ─────────────────────────────────────────────────────────────────── */

.card {
  background-color: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: 10px;
  padding: 24px;
  box-shadow: var(--color-shadow-card);
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.sectionTitle {
  font-size: 1rem;
  font-weight: 600;
  font-family: var(--font-family-base);
  color: var(--color-text-primary);
  margin: 0;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--color-border);
}

/* ── Form fields ──────────────────────────────────────────────────────────── */

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.label {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text-secondary);
}

/* ── Theme toggle ─────────────────────────────────────────────────────────── */

.themeRow {
  display: flex;
  gap: 10px;
}

.themeOption {
  flex: 1;
  background: none;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s, background-color 0.15s;
  font-family: inherit;
}

.themeOption:hover {
  border-color: var(--color-accent);
  color: var(--color-text-primary);
}

.themeOptionActive {
  border-color: var(--color-accent);
  background-color: var(--color-accent-subtle);
  color: var(--color-text-primary);
  font-weight: 600;
}

/* ── Language select ──────────────────────────────────────────────────────── */

.select {
  background-color: var(--color-bg-secondary);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 0.9rem;
  font-family: inherit;
  cursor: pointer;
  max-width: 240px;
}

.select:focus {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
}

/* ── Save row ─────────────────────────────────────────────────────────────── */

.saveRow {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.savedMsg {
  font-size: 0.875rem;
  color: var(--color-text-secondary);
}

.errorMsg {
  font-size: 0.875rem;
  color: var(--color-accent);
}

/* ── Buttons ──────────────────────────────────────────────────────────────── */

.buttonRow {
  display: flex;
  gap: 12px;
}

.primaryBtn {
  background-color: var(--color-accent);
  color: var(--color-text-inverse);
  border: none;
  border-radius: 6px;
  padding: 9px 20px;
  font-size: 0.9rem;
  font-family: inherit;
  cursor: pointer;
  transition: background-color 0.2s;
  white-space: nowrap;
}

.primaryBtn:hover:not(:disabled) {
  background-color: var(--color-accent-hover);
}

.primaryBtn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.signOutBtn {
  background: none;
  color: var(--color-accent);
  border: 1px solid var(--color-accent);
  border-radius: 6px;
  padding: 9px 20px;
  font-size: 0.9rem;
  font-family: inherit;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
  white-space: nowrap;
}

.signOutBtn:hover {
  background-color: var(--color-accent);
  color: var(--color-text-inverse);
}

/* ── Account actions list ─────────────────────────────────────────────────── */

.actionList {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.actionLink {
  font-size: 0.9rem;
  color: var(--color-accent);
  transition: color 0.15s;
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  cursor: pointer;
}

.actionLink:hover {
  color: var(--color-accent-hover);
  text-decoration: underline;
}

.actionButton {
  background-color: var(--color-accent-subtle);
  color: var(--color-accent);
  border: 1px solid var(--color-accent);
  border-radius: 6px;
  padding: 9px 20px;
  font-size: 0.9rem;
  font-family: inherit;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s, border-color 0.2s;
  white-space: nowrap;
}

.actionButton:hover:not(:disabled) {
  background-color: var(--color-accent);
  color: var(--color-text-inverse);
}

.actionButton:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.actionDisabled {
  font-size: 0.9rem;
  color: var(--color-text-muted);
}

/* ── In-app password reset form ───────────────────────────────────────────── */

.resetForm {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 320px;
}

.resetHint {
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  margin: 0;
}

.textInput {
  background-color: var(--color-bg-secondary);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 0.9rem;
  font-family: inherit;
  width: 100%;
  box-sizing: border-box;
}

.textInput:focus {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
}

/* ── "Not signed in" state ───────────────────────────────────────────────── */

.notSignedIn {
  color: var(--color-text-secondary);
  margin: 0 0 4px;
}

/* ── Danger zone ──────────────────────────────────────────────────────────── */

.dangerZone {
  background-color: var(--color-accent-subtle);
  border: 1px solid var(--color-accent);
  border-radius: 10px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.dangerZoneTitle {
  font-size: 1rem;
  font-weight: 600;
  font-family: var(--font-family-base);
  color: var(--color-accent);
  margin: 0;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--color-accent);
}

.deleteBtn {
  align-self: flex-start;
  background: none;
  color: var(--color-accent);
  border: 1px solid var(--color-accent);
  border-radius: 6px;
  padding: 9px 20px;
  font-size: 0.9rem;
  font-family: inherit;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
  white-space: nowrap;
}

.deleteBtn:hover {
  background-color: var(--color-accent);
  color: var(--color-text-inverse);
}

/* ── Back link ────────────────────────────────────────────────────────────── */

.backLink {
  display: inline-block;
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  margin-top: 4px;
  transition: color 0.15s;
}

.backLink:hover {
  color: var(--color-accent);
}
